/**
 * Market view panel (§A.4, OBS-5). Binds to `index.json.refs.market`
 * (`RoleSnapshot.market()` — verified live 2026-09-27, real `market.view`
 * events are now published: ~900 on the male organism's log at the time of
 * this pass): one card per attended slot (symbol, last price, bid/ask +
 * spread, session state, staleness age), a compact order-book ladder when
 * the payload carries L2 depth (crypto), the declared universe, and a
 * polling "last updated" clock — with a friendly placeholder when nothing
 * has been published yet. No raw JSON in the normal view; a small "raw"
 * dev toggle can still show the fetched document verbatim for debugging.
 *
 * Owner feedback 2026-09-27: "the market view pane doesn't make sense, its
 * literally just code" — the previous version of this panel rendered the
 * fetched document as a `<pre>` of JSON. This rewrite replaces that with a
 * designed layout; a raw dump still exists, but only behind an explicit,
 * off-by-default toggle (requirement: "no raw JSON anywhere in the UI; a
 * raw toggle may exist behind a small dev control").
 */

import type { RoleSnapshot } from "../data/snapshotIndex";
import type { MarketDocument, MarketLadderLevel, MarketSlot } from "../types/incremental";
import { renderNotPublishedYet } from "./NotPublishedYet";
import {
  buildMarketPanelState,
  commonLadderReason,
  declaredUniverseText,
  hasLadder,
  isLiveSession,
  lastPriceText,
  quoteSpreadText,
  sessionStateLabel,
  stalenessDotClass,
  stalenessText,
} from "./marketView";
import { Poller, freshnessBadgeLabel } from "../data/polling";
import { isNotPublishedYet } from "../data/fetchJson";
import { config } from "../config";
import { attachTooltip } from "../ui/tooltip";

// 3, not 5: the compact quote-tape tile (orchestrator course correction,
// 2026-09-28) has far less vertical budget than the old stacked card did.
const LADDER_MAX_LEVELS = 3;

export class MarketPanel {
  private poller: Poller<MarketDocument | null> | null = null;
  private rawVisible = false;
  private lastDocument: MarketDocument | null = null;
  private freshnessEl: HTMLSpanElement | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly role: RoleSnapshot,
  ) {}

  async mount(): Promise<void> {
    this.container.innerHTML = "";

    const initial = await this.safeFetch();
    this.lastDocument = initial;
    this.render(initial);

    // OBS-5's market view is a live, per-cycle feed just like the log and
    // brain panels (§ "feels realtime" architecture) — poll it on the same
    // cadence rather than requiring a reload to see a fresher quote.
    this.poller = new Poller<MarketDocument | null>(
      async () => {
        const doc = await this.safeFetch();
        const key = doc ? `${doc.as_of_seq}:${doc.slots.length}` : "none";
        return { key, value: doc };
      },
      (snapshot) => {
        this.lastDocument = snapshot.value;
        this.render(snapshot.value);
        if (this.freshnessEl) {
          this.freshnessEl.textContent = freshnessBadgeLabel(
            snapshot.status,
            snapshot.lastSuccessAt,
          );
        }
      },
      { intervalMs: config.pollIntervalMs, backoffMaxMs: config.pollBackoffMaxMs },
      isNotPublishedYet,
    );
    this.poller.start();
  }

  private async safeFetch(): Promise<MarketDocument | null> {
    try {
      return await this.role.market();
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  private render(doc: MarketDocument | null): void {
    this.container.innerHTML = "";
    const state = buildMarketPanelState(doc);

    if (state.kind === "empty") {
      this.container.appendChild(
        renderNotPublishedYet(
          "Market view",
          "Waiting for the first market view — OBS-5 publishes one market.view event per attended " +
            "slot, per cycle; this panel renders a card per slot the moment the role's index.json " +
            "names a market ref with at least one slot.",
        ),
      );
      return;
    }

    const root = document.createElement("div");
    root.className = "obs-market";

    root.appendChild(this.buildHeader(state.slots, state.asOfSeq));

    // Owner requirement 2026-09-27 (polish round): the ladder-less feed note
    // ("IEX top-of-book only — no depth feed") repeated identically on every
    // card — a universe is overwhelmingly one feed, so show it once here
    // instead. A genuinely mixed universe (differing reasons) falls back to
    // the honest per-card note (`buildCard`) rather than hiding a real
    // difference.
    const sharedReason = commonLadderReason(state.slots);
    if (sharedReason) root.appendChild(this.buildFeedNote(sharedReason));

    const grid = document.createElement("div");
    grid.className = "obs-market-grid";
    for (const slot of state.slots) grid.appendChild(this.buildCard(slot, sharedReason));
    root.appendChild(grid);

    if (this.rawVisible && doc) {
      const pre = document.createElement("pre");
      pre.className = "obs-raw-json";
      pre.style.margin = "0 12px 12px";
      pre.textContent = JSON.stringify(doc, null, 2);
      root.appendChild(pre);
    }

    this.container.appendChild(root);
  }

  private buildHeader(slots: readonly MarketSlot[], asOfSeq: number): HTMLDivElement {
    const header = document.createElement("div");
    header.className = "obs-market-header";

    const universe = document.createElement("span");
    universe.textContent = `Universe (${slots.length}): ${declaredUniverseText(slots)}`;
    attachTooltip(
      universe,
      "Every symbol this organism attends to this cycle, per its declared slot universe.",
    );
    header.appendChild(universe);

    const asOf = document.createElement("span");
    asOf.textContent = `as of seq ${asOfSeq}`;
    attachTooltip(
      asOf,
      "The catalog sequence number this market view was folded from — find this seq in the log panel for the raw event.",
    );
    header.appendChild(asOf);

    const freshness = document.createElement("span");
    freshness.className = "obs-freshness";
    freshness.textContent = "live";
    this.freshnessEl = freshness;
    header.appendChild(freshness);

    const rawToggle = document.createElement("button");
    rawToggle.className = "obs-raw-toggle";
    rawToggle.textContent = this.rawVisible ? "hide raw" : "{ } raw";
    attachTooltip(
      rawToggle,
      "Dev control: show the exact fetched market document as JSON, for debugging.",
    );
    rawToggle.addEventListener("click", () => {
      this.rawVisible = !this.rawVisible;
      this.render(this.lastDocument);
    });
    header.appendChild(rawToggle);

    return header;
  }

  /** The ladder-less feed note, shown once for the whole panel instead of
   * repeated on every card (see `render`'s call site). */
  private buildFeedNote(reason: string): HTMLDivElement {
    const note = document.createElement("div");
    note.className = "obs-market-feed-note";
    note.textContent = ladderReasonText(reason);
    attachTooltip(
      note,
      "Why the cards below show a price ladder or not — this applies to every ladder-less slot in this universe.",
    );
    return note;
  }

  /** A compact tile in the horizontal quote tape (orchestrator course
   * correction, 2026-09-28: "replace the tall stacked cards ... with a
   * compact horizontal quote tape/row of tiles: symbol, last, bid×ask,
   * spread, a small session pill in plain words, staleness dot. All 5
   * symbols visible at a glance"). Same fields as the old stacked card,
   * denser layout — nothing dropped, a ladder-bearing slot (crypto) still
   * gets its ladder, just underneath the compact header instead of after a
   * much taller stack of one-fact-per-line rows. */
  private buildCard(slot: MarketSlot, sharedLadderReason: string | null): HTMLDivElement {
    const card = document.createElement("div");
    card.className = "obs-market-tile";
    if (slot.gap) card.classList.add("obs-market-gap");

    const top = document.createElement("div");
    top.className = "obs-market-tile-top";
    const symbol = document.createElement("span");
    symbol.className = "obs-market-symbol";
    symbol.textContent = slot.symbol;
    attachTooltip(
      symbol,
      `Feed: ${slot.feed}${slot.delay_ms !== null ? ` · measured delay ${slot.delay_ms.toFixed(0)}ms` : ""}`,
    );
    const price = lastPriceText(slot);
    const priceEl = document.createElement("span");
    priceEl.className = price.known
      ? "obs-market-price obs-num-display"
      : "obs-market-price obs-market-empty";
    priceEl.textContent = price.text;
    attachTooltip(
      priceEl,
      "Last real trade print if one exists (full precision; a sub-$10 coin whose print was only recorded in cents shows the exact bid/ask midpoint instead), otherwise the bid/ask midpoint — never a fabricated price.",
    );
    top.appendChild(symbol);
    top.appendChild(priceEl);
    card.appendChild(top);

    const spread = quoteSpreadText(slot.latest_quote);
    const spreadEl = document.createElement("div");
    spreadEl.className = "obs-market-tile-spread";
    spreadEl.textContent = spread ?? "no quote yet";
    attachTooltip(spreadEl, "Top-of-book bid / ask, and the computed spread between them.");
    card.appendChild(spreadEl);

    const meta = document.createElement("div");
    meta.className = "obs-market-tile-meta";
    const sessionBadge = document.createElement("span");
    sessionBadge.className = `obs-market-session ${isLiveSession(slot.session_state) ? "obs-session-live" : "obs-session-dim"}`;
    sessionBadge.textContent = sessionStateLabel(slot.session_state);
    attachTooltip(
      sessionBadge,
      `organism.calendar.SessionState: ${slot.session_state} — only "regular" (or a market that never closes) is drawn as live.`,
    );
    meta.appendChild(sessionBadge);

    const stalenessDot = document.createElement("span");
    stalenessDot.className = `obs-market-staleness-dot ${stalenessDotClass(slot, price.known)}`;
    attachTooltip(
      stalenessDot,
      price.known || slot.gap
        ? `${stalenessText(slot)} — 0 = fresh this cycle; above 1.0 means a live data gap was detected independently of this value.`
        : "No quote has been received for this symbol yet — nothing to measure staleness against.",
    );
    meta.appendChild(stalenessDot);
    card.appendChild(meta);

    if (hasLadder(slot) && slot.ladder) {
      card.appendChild(this.buildLadder(slot.ladder.bids, slot.ladder.asks));
    } else if (slot.ladder_reason && slot.ladder_reason !== sharedLadderReason) {
      // Only rendered when this slot's reason *differs* from the one
      // already shown once in the panel header (`buildFeedNote`) — the
      // common case (every slot sharing one feed) never reaches here.
      const note = document.createElement("div");
      note.className = "obs-market-ladder-note";
      note.textContent = ladderReasonText(slot.ladder_reason);
      card.appendChild(note);
    }

    return card;
  }

  private buildLadder(
    bids: readonly MarketLadderLevel[],
    asks: readonly MarketLadderLevel[],
  ): HTMLDivElement {
    // design/reshape/RESHAPE.md §6 (AUDIT.md finding 6): the ladder used to
    // show two bare columns of numbers with no header — readable only after
    // consulting the tooltip. `outer` wraps the new header row + the
    // existing grid so `buildCard`'s single `appendChild` call is unchanged.
    const outer = document.createElement("div");

    const headers = document.createElement("div");
    headers.className = "obs-market-ladder-headers";
    const bidHeader = document.createElement("span");
    bidHeader.textContent = "bid";
    const askHeader = document.createElement("span");
    askHeader.textContent = "ask";
    headers.appendChild(bidHeader);
    headers.appendChild(askHeader);
    outer.appendChild(headers);

    const wrap = document.createElement("div");
    wrap.className = "obs-market-ladder";
    attachTooltip(
      wrap,
      "Top-of-book L2 depth: bids on the left, asks on the right, most aggressive price first.",
    );

    const bidSide = document.createElement("div");
    bidSide.className = "obs-market-ladder-side";
    const maxBidSize = Math.max(...bids.map((l) => Number.parseFloat(l.size) || 0), 1);
    for (const level of bids.slice(0, LADDER_MAX_LEVELS)) {
      bidSide.appendChild(this.buildLadderLevel(level, "obs-market-ladder-bid", maxBidSize));
    }

    const askSide = document.createElement("div");
    askSide.className = "obs-market-ladder-side";
    const maxAskSize = Math.max(...asks.map((l) => Number.parseFloat(l.size) || 0), 1);
    for (const level of asks.slice(0, LADDER_MAX_LEVELS)) {
      askSide.appendChild(this.buildLadderLevel(level, "obs-market-ladder-ask", maxAskSize));
    }

    wrap.appendChild(bidSide);
    wrap.appendChild(askSide);
    outer.appendChild(wrap);
    return outer;
  }

  private buildLadderLevel(
    level: MarketLadderLevel,
    side: "obs-market-ladder-bid" | "obs-market-ladder-ask",
    maxSize: number,
  ): HTMLDivElement {
    const row = document.createElement("div");
    row.className = `obs-market-ladder-level ${side}`;
    const fraction = Math.max(0, Math.min(1, (Number.parseFloat(level.size) || 0) / maxSize));
    const tint = side === "obs-market-ladder-bid" ? "74,222,128" : "248,113,113";
    row.style.background = `linear-gradient(to right, rgba(${tint},0.22) ${(fraction * 100).toFixed(0)}%, transparent ${(fraction * 100).toFixed(0)}%)`;
    const price = document.createElement("span");
    price.textContent = level.price;
    const size = document.createElement("span");
    size.textContent = level.size;
    row.appendChild(price);
    row.appendChild(size);
    return row;
  }

  dispose(): void {
    this.poller?.stop();
  }
}

/** The human-readable text for one `ladder_reason` value — shared by
 * `buildFeedNote` (the once-per-panel header note) and `buildCard`'s
 * fallback (a slot whose reason genuinely differs from the header's). */
function ladderReasonText(reason: string): string {
  return reason === "iex_top_of_book_only"
    ? "IEX top-of-book only — no depth feed"
    : "No L2 feed wired yet";
}
